/**
 * Route table.
 *
 * One declaration drives the router, the public navigation bar, the admin
 * sidebar and the editor's "view on site" link. Adding a page in one place
 * rather than four is what stops the nav and the router drifting apart.
 *
 * Layer rule: paths and i18n keys only — no components, no styling. The router
 * imports the components; this file just names the shape of the site.
 *
 * 路由表。
 *
 * 一处声明同时驱动路由器、公开导航栏、后台侧栏，以及编辑器的「在网站上查看」链接。
 * 「加页面只改一处而不是四处」正是导航和路由不会走散的原因。
 *
 * 分层规则：只有路径和文案 key——不含组件、不含样式。组件由路由器负责引入，
 * 本文件只描述站点的形状。
 */

/** Public routes, in navigation order. 公开路由，按导航顺序排列。 */
export const PUBLIC_NAV = [
  { path: '/', key: 'nav.home', exact: true },
  { path: '/research', key: 'nav.research' },
  { path: '/publications', key: 'nav.publications' },
  { path: '/achievements', key: 'nav.achievements' },
  { path: '/members', key: 'nav.members' },
  { path: '/news', key: 'nav.news' },
  { path: '/contact', key: 'nav.contact' },
]

/**
 * Where a record of each collection lives on the public site.
 *
 * The editor uses this to build a preview / "view on site" link without
 * hardcoding a path per collection.
 *
 * 每个集合的记录在公开站点上的位置。编辑器据此生成「在网站上查看」链接，
 * 不必为每个集合硬编码路径。
 */
export const COLLECTION_PUBLIC_PATH = {
  news: (record) => `/news/${record.id}`,
  research: (record) => `/research/projects/${record.id}`,
  demos: (record) => `/research/demos/${record.id}`,
  members: () => '/members',
  publications: () => '/publications',
  patents: () => '/achievements',
  awards: () => '/achievements',
}

/** Admin section paths. 后台各区路径。 */
export const ADMIN_PATHS = {
  login: '/admin/login',
  dashboard: '/admin',
  collection: (key) => `/admin/c/${key}`,
  newRecord: (key) => `/admin/c/${key}/new`,
  editRecord: (key, id) => `/admin/c/${key}/${id}`,
  media: '/admin/media',
  users: '/admin/users',
  settings: '/admin/settings',
  appearance: '/admin/appearance',
  stats: '/admin/stats',
  mcp: '/admin/mcp',
  account: '/admin/account',
}

/**
 * Admin sidebar groups. `adminOnly` entries are hidden from editors entirely
 * rather than shown-and-disabled — a control you can see but never use is worse
 * than one that is not there.
 *
 * 后台侧栏分组。adminOnly 项对编辑完全隐藏，而不是「显示但禁用」——
 * 看得见却永远用不了的控件比不存在更糟。
 */
export const ADMIN_NAV = [
  {
    groupKey: 'admin.content',
    items: [{ path: ADMIN_PATHS.dashboard, key: 'admin.dashboard', exact: true }],
    // Collection entries are appended at render time from COLLECTION_ORDER so
    // adding a collection to the schema also adds it to the sidebar.
    // 集合条目在渲染时按 COLLECTION_ORDER 追加，这样在 schema 里加一个集合，
    // 侧栏也会自动多一项。
    includeCollections: true,
  },
  {
    groupKey: 'admin.system',
    items: [
      { path: ADMIN_PATHS.media, key: 'admin.media' },
      { path: ADMIN_PATHS.users, key: 'admin.users', adminOnly: true },
      { path: ADMIN_PATHS.settings, key: 'admin.settings', adminOnly: true },
      { path: ADMIN_PATHS.appearance, key: 'admin.appearance', adminOnly: true },
      { path: ADMIN_PATHS.stats, key: 'stats.title', adminOnly: true },
      // No `adminOnly`, and now doubly so: the page is documentation, the person
      // who will review an AI's drafts is an editor, and it is also where anyone
      // mints their own API key. Making it admin-only would put an admin back in
      // the middle of every editor's setup — the exact errand keys removed.
      // 不加 adminOnly，现在更有双重理由：这一页是文档，真正要去审 AI 草稿的人是编辑，
      // 而且每个人自助签发 API 密钥也在这一页。设成仅管理员，等于把管理员重新塞回
      // 每个编辑的配置流程里——而密钥取消的正是这趟差事。
      { path: ADMIN_PATHS.mcp, key: 'mcp.title' },
    ],
  },
]
